<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';

import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import Avatar from 'next/avatar/Avatar.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
export default {
  components: {
    SettingsPageLayout,
    SettingsSectionCard,
    NextButton,
    AccountId,
    BuildInfo,
    AccountDelete,
    WithLabel,
    NextInput,
    Avatar,
  },
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    async initializeAccount() {
      try {
        const { name, locale, id, domain, support_email, features } =
          this.getAccount(this.accountId);

        this.$root.$i18n.locale = this.uiSettings?.locale || locale;
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
      } catch (error) {
        // Ignore error
      }
    },

    async updateLogo({ file }) {
      try {
        await this.$store.dispatch('accounts/updateWithAvatar', {
          accountId: this.accountId,
          avatar: file,
        });
        useAlert(this.$t('GENERAL_SETTINGS.LOGO_SECTION.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.LOGO_SECTION.ERROR'));
      }
    },
    async deleteLogo() {
      try {
        await this.$store.dispatch('accounts/deleteAvatar', this.accountId);
        useAlert(this.$t('GENERAL_SETTINGS.LOGO_SECTION.DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.LOGO_SECTION.ERROR'));
      }
    },
    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
        });
        if (this.uiSettings?.locale) {
          this.$root.$i18n.locale = this.uiSettings?.locale;
        } else {
          this.$root.$i18n.locale = this.locale;
        }
        this.getAccount(this.id).locale = this.locale;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },
  },
};
</script>

<template>
  <SettingsPageLayout :title="$t('GENERAL_SETTINGS.TITLE')">
    <SettingsSectionCard
      :title="$t('GENERAL_SETTINGS.LOGO_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.LOGO_SECTION.NOTE')"
    >
      <div class="flex items-center gap-4">
        <Avatar
          :size="72"
          :name="name || currentAccount.name || ''"
          :src="currentAccount.avatar_url || ''"
          allow-upload
          rounded-full
          @upload="updateLogo"
          @delete="deleteLogo"
        />
        <div class="text-sm text-n-slate-11">
          <p>{{ $t('GENERAL_SETTINGS.LOGO_SECTION.FORMATS') }}</p>
          <p v-if="!currentAccount.avatar_url" class="mt-1">
            {{ $t('GENERAL_SETTINGS.LOGO_SECTION.UPLOAD_HINT') }}
          </p>
        </div>
      </div>
    </SettingsSectionCard>

    <SettingsSectionCard
      :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
    >
      <form
        v-if="!uiFlags.isFetchingItem"
        class="grid gap-4"
        @submit.prevent="updateAccount"
      >
        <WithLabel
          :has-error="v$.name.$error"
          :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
          :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
        >
          <NextInput
            v-model="name"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </WithLabel>
        <WithLabel
          :has-error="v$.locale.$error"
          :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
          :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
        >
          <select
            v-model="locale"
            class="!mb-0 text-sm w-full px-3 py-2 rounded-lg border border-n-slate-5 bg-n-slate-2 text-n-slate-12"
          >
            <option
              v-for="lang in languagesSortedByCode"
              :key="lang.iso_639_1_code"
              :value="lang.iso_639_1_code"
            >
              {{ lang.name }}
            </option>
          </select>
        </WithLabel>
        <WithLabel
          v-if="featureCustomReplyDomainEnabled"
          :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
        >
          <NextInput
            v-model="domain"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
          />
          <template #help>
            {{
              featureInboundEmailEnabled &&
              $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
            }}
            {{
              featureCustomReplyDomainEnabled &&
              $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
            }}
          </template>
        </WithLabel>
        <WithLabel
          v-if="featureCustomReplyEmailEnabled"
          :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
        >
          <NextInput
            v-model="supportEmail"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')"
          />
        </WithLabel>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
      <div v-else class="flex items-center justify-center py-8">
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
      </div>
    </SettingsSectionCard>

    <AccountId />
    <AccountDelete v-if="!uiFlags.isFetchingItem && isOnChatwootCloud" />
    <BuildInfo />
  </SettingsPageLayout>
</template>
